import { create } from 'zustand';
import axiosClient from '../services/api';

// Helper to generate upload ID
const generateUploadId = () => `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const useMediaStore = create((set, get) => ({
    videos: [],
    resources: [],
    folders: [],
    isLoading: false,
    error: null,

    // Video Actions
    fetchVideos: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await axiosClient.get('/videos/myvideos');
            set({ videos: response.data.data, isLoading: false });
        } catch (error) {
            set({ error: error.response?.data?.error || 'Failed to fetch videos', isLoading: false });
        }
    },

    fetchVideosForUser: async (uploadedBy) => {
        set({ isLoading: true, error: null });
        try {
            const response = await axiosClient.get(`/videos/myvideos?uploadedBy=${uploadedBy}`);
            set({ videos: response.data.data, isLoading: false });
        } catch (error) {
            set({ error: error.response?.data?.error || 'Failed to fetch videos', isLoading: false });
        }
    },

    fetchResources: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await axiosClient.get('/resources');
            set({ resources: response.data.data, isLoading: false });
        } catch (error) {
            set({ error: error.response?.data?.error || 'Failed to fetch resources', isLoading: false });
        }
    },

    fetchResourcesForUser: async (uploadedBy) => {
        set({ isLoading: true, error: null });
        try {
            const response = await axiosClient.get(`/resources?uploadedBy=${uploadedBy}`);

            set({ resources: response.data.data, isLoading: false });
        } catch (error) {
            set({ error: error.response?.data?.error || 'Failed to fetch resources', isLoading: false });
        }
    },


    fetchFolders: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await axiosClient.get('/folders');
            const folders = response.data.data.allFolders || [];
            set({ folders: ['All', ...folders], isLoading: false });
        } catch (error) {
            set({ error: error.response?.data?.error || 'Failed to fetch folders', isLoading: false });
        }
    },

    fetchFoldersForUser: async (uploadedBy) => {
        set({ isLoading: true, error: null });
        try {
            const response = await axiosClient.get(`/folders?uploadedBy=${uploadedBy}`);

            const folders = response.data.data.allFolders || [];
            set({ folders: ['All', ...folders], isLoading: false });
        } catch (error) {
            set({ error: error.response?.data?.error || 'Failed to fetch folders', isLoading: false });
        }
    },


    // Add to your existing store object
    renameFolder: async (oldPath, newPath) => {
        try {
            const response = await axiosClient.put('/folders/rename', {
                oldPath,
                newPath
            });

            if (response.data.success) {
                // Optimistically update UI
                set(state => {
                    const updatedFolders = state.folders.map(f =>
                        f === oldPath ? newPath : f
                    );
                    return { folders: updatedFolders };
                });
                get().fetchFolders(); // Sync with backend
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error renaming folder:', error);
            return false;
        }
    },

    deleteFolder: async (folderPath) => {
        try {
            const response = await axiosClient.delete('/folders', {
                folderPath
            });

            if (response.data.success) {
                // Optimistically update UI
                set(state => ({
                    folders: state.folders.filter(f => f !== folderPath)
                }));
                get().fetchFolders(); // Sync with backend
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error deleting folder:', error);
            return false;
        }
    },

    // Chunked video upload
    uploadVideo: async (file, folder, onProgress) => {
        set({ isLoading: true, error: null });

        try {
            const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks
            const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
            const uploadId = generateUploadId();
            const fileName = file.name;
            let videoId = null;

            // Upload chunks sequentially
            for (let chunkNumber = 1; chunkNumber <= totalChunks; chunkNumber++) {
                const start = (chunkNumber - 1) * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file.size);
                const chunk = file.slice(start, end);

                const formData = new FormData();
                formData.append('video', chunk);
                formData.append('uploadId', uploadId);
                formData.append('chunkNumber', chunkNumber);
                formData.append('totalChunks', totalChunks);
                formData.append('fileName', fileName);
                if (chunkNumber === 1) {
                    formData.append('folder', folder || 'Uncategorized');
                }
                if (videoId) {
                    formData.append('videoId', videoId);
                }

                const response = await axiosClient.post('/videos/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (progressEvent) => {
                        if (onProgress) {
                            const chunkProgress = (progressEvent.loaded / progressEvent.total) * 100;
                            const totalProgress = ((chunkNumber - 1) + chunkProgress / 100) / totalChunks * 100;
                            onProgress(Math.round(totalProgress));
                        }
                    }
                });

                // Store videoId from first chunk response
                if (chunkNumber === 1 && response.data?.data?.videoId) {
                    videoId = response.data.data.videoId;
                }

                // Handle last chunk
                if (chunkNumber === totalChunks) {
                    set({ isLoading: false });
                    get().fetchVideos();
                    return response.data.data;
                }
            }
        } catch (error) {
            set({
                error: error.response?.data?.error || 'Upload failed',
                isLoading: false
            });
            throw error;
        }
    },

    uploadResource: async (file, title, description, folder) => {
        set({ isLoading: true, error: null });

        const formData = new FormData();
        formData.append('file', file);
        if (title) formData.append('title', title);
        if (description) formData.append('description', description);
        if (folder) formData.append('folder', folder);

        try {
            const response = await axiosClient.post('/resources', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            set({ isLoading: false });
            get().fetchResources();
            return response.data.data;
        } catch (error) {
            set({ error: error.response?.data?.error || 'Upload failed', isLoading: false });
        }
    },

    deleteVideo: async (videoId) => {
        try {
            await axiosClient.delete(`/videos/${videoId}`);
            get().fetchVideos(); // Refresh the list
        } catch (error) {
            set({ error: error.response?.data?.error || 'Failed to delete video' });
        }
    },

    deleteResource: async (resourceId) => {
        try {
            await axiosClient.delete(`/resources/${resourceId}`);
            get().fetchResources(); // Refresh the list
        } catch (error) {
            set({ error: error.response?.data?.error || 'Failed to delete resource' });
        }
    },

    clearError: () => set({ error: null }),
}));

export default useMediaStore;